import type { CSSProperties, FC, ReactNode } from 'react'
import type { Tone } from '@/types'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { useScrollProgress } from '@/hooks/use-scroll-progress'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitWords } from '@/components/generals/split-words'

interface Step {
    when: string
    title: string
    text: string
}

interface GettingStartedProps {
    index?: string
    title?: string
    intro: string
    steps: Step[]
    tone?: Tone
    id?: string
}

const DARK: Tone[] = ['night', 'deep', 'raised']

/** Onboarding as a measured timeline: a tape runs between the steps as you scroll. */
export const GettingStarted: FC<GettingStartedProps> = ({
    index,
    title = 'Slik kommer du i gang',
    intro,
    steps,
    tone = 'paper',
    id = 'kom-i-gang-steg'
}): ReactNode => {
    const ref = useRef<HTMLOListElement>(null)

    useScrollProgress(ref, { startAt: 0.85, endAt: 0.6 })

    return (
        <section
            id={id}
            className={cn(`tone-${tone}`, DARK.includes(tone) && 'grain', 'relative py-24 md:py-36')}
            data-header={DARK.includes(tone) ? 'dark' : 'light'}
        >
            <div className='wrap'>
                <div className='grid grid-cols-12 gap-x-6 gap-y-8'>
                    <div className='col-span-12 lg:col-span-6'>
                        <SectionLabel index={index}>Oppstart</SectionLabel>
                        <h2 className='t-h1 mt-6 balance' data-reveal='words'>
                            <SplitWords text={title} />
                        </h2>
                    </div>
                    <p className='col-span-12 self-end t-lead text-tone-muted pretty lg:col-span-4 lg:col-start-9'>{intro}</p>
                </div>

                <ol ref={ref} className='steps-line relative mt-16 grid grid-cols-1 gap-x-6 gap-y-12 md:mt-24 md:grid-cols-3'>
                    <span className='steps-tape pointer-events-none absolute left-0 right-0 top-[0.4375rem] hidden md:block' aria-hidden='true' />

                    {steps.map((step, i) => (
                        <li key={step.title} className='relative' data-reveal='rise' style={{ '--d': i } as CSSProperties}>
                            <span className='relative z-10 flex items-center gap-4'>
                                <span className='block size-3.5 bg-tone-accent' aria-hidden='true' />
                                <span className='t-label tabular text-tone-muted'>{String(i + 1).padStart(2, '0')} · {step.when}</span>
                            </span>
                            <h3 className='t-h3 mt-8 max-w-[18ch] balance'>{step.title}</h3>
                            <p className='mt-4 max-w-sm text-tone-muted pretty'>{step.text}</p>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    )
}
